/** Standalone start screen: drafts, a new graphic (blank or from a starter template), and the conformance samples as examples. */
import { useEffect, useState } from 'react';
import { FilePlus2, LayoutTemplate, Shapes, Trash2 } from 'lucide-react';
import { api, type DraftSummary, type Example } from './api.ts';
import { t } from './i18n.ts';
import { aspectOf, rateFor, ratesFor, sameAspect } from '@prism-graphics/scene-schema';
import { AspectBadge, FormatOptions, LayoutChoice, RateOptions, parseRate, parseSize, rateValue, sizeKey } from './ui/formats.tsx';
import { TemplatePicker } from './TemplatePicker.tsx';
import { VersionLabel } from './version.tsx';

export function StartScreen({ onOpen }: { onOpen: (id: string) => void }) {
  const [drafts, setDrafts] = useState<DraftSummary[]>();
  const [examples, setExamples] = useState<Example[]>([]);
  const [error, setError] = useState<string>();
  const [name, setName] = useState('');
  const [size, setSize] = useState({ width: 1920, height: 1080 });
  const [rate, setRate] = useState({ num: 50, den: 1 });
  // Further layouts (ADR 0037): the format select is the primary.
  const [layouts, setLayouts] = useState<Array<{ width: number; height: number }>>([]);
  const [busy, setBusy] = useState(false);
  const [hasBrand, setHasBrand] = useState(false);

  const load = () => {
    api.listDrafts().then((r) => setDrafts(r.items), (e: Error) => setError(e.message));
    api.examples().then((r) => setExamples(r.items), () => undefined);
    api.brandKit().then((b) => setHasBrand(b.revision > 0), () => undefined);
  };
  useEffect(load, []);

  const create = async (body: Parameters<typeof api.createDraft>[0]) => {
    setBusy(true);
    try {
      const d = await api.createDraft(body);
      onOpen(d.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="start">
      <header className="start-head">
        <div className="brand">
          PRISM <b>Graphics</b>
        </div>
        <p className="muted">{t('start.tagline')}</p>
      </header>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="start-grid">
        <section className="card" aria-labelledby="new-h">
          <h2 id="new-h">
            <FilePlus2 size={16} aria-hidden /> {t('start.new')}
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void create({ ...(name.trim() ? { name: name.trim() } : {}), canvas: { ...size, frameRate: rate }, ...(layouts.length ? { layouts } : {}) });
            }}
          >
            <label className="field text">
              <span className="field-label">{t('start.name')}</span>
              <input type="text" value={name} placeholder={t('start.namePlaceholder')} onChange={(e) => setName(e.target.value)} data-testid="new-name" />
            </label>
            <div className="row two">
              <label className="field select">
                <span className="field-label">{t('top.resolution')}</span>
                <select
                  value={sizeKey(size)}
                  data-testid="new-format"
                  onChange={(e) => {
                    const next = parseSize(e.target.value);
                    setSize(next);
                    setRate(rateFor(next, rate));
                    setLayouts(layouts.filter((l) => !sameAspect(l, next)));
                  }}
                >
                  <FormatOptions />
                </select>
              </label>
              <label className="field select">
                <span className="field-label">{t('top.frameRate')}</span>
                <select value={rateValue(rate, ratesFor(size))} data-testid="new-rate" onChange={(e) => setRate(parseRate(e.target.value))}>
                  <RateOptions rates={ratesFor(size)} />
                </select>
              </label>
            </div>
            <LayoutChoice primary={size} value={layouts} onChange={setLayouts} />
            <button type="submit" className="btn primary" disabled={busy} data-testid="new-blank">
              {t('start.create')}
            </button>
          </form>
        </section>
        <section className="card wide" aria-labelledby="tpl-h">
          <h2 id="tpl-h">
            <LayoutTemplate size={16} aria-hidden /> {t('templates.title')}
          </h2>
          <p className="hint">{t('templates.hint')}</p>
          <TemplatePicker aspect={aspectOf(size)} busy={busy} brandAvailable={hasBrand} onPick={(template, brand) => void create({ template, brand, ...(name.trim() ? { name: name.trim() } : {}), canvas: { ...size, frameRate: rate }, ...(layouts.length ? { layouts } : {}) })} />
        </section>
        <section className="card" aria-labelledby="ex-h">
          <h2 id="ex-h">
            <Shapes size={16} aria-hidden /> {t('start.examples')}
          </h2>
          <p className="hint">{t('start.examplesHint')}</p>
          <ul className="list">
            {examples.map((ex) => (
              <li key={ex.id}>
                <button type="button" className="list-item" onClick={() => void create({ example: ex.id })} data-testid={`example-${ex.id}`}>
                  <span className="li-title">{ex.name}</span>
                  <span className="li-meta">
                    <AspectBadge canvas={ex.canvas} /> {ex.canvas.width} × {ex.canvas.height} · {Math.round((ex.canvas.frameRate.num / ex.canvas.frameRate.den) * 100) / 100} fps
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className="card wide" aria-labelledby="drafts-h">
          <h2 id="drafts-h">{t('start.drafts')}</h2>
          {drafts === undefined ? (
            <p className="muted">{t('editor.loading')}</p>
          ) : drafts.length === 0 ? (
            <p className="muted">{t('start.noDrafts')}</p>
          ) : (
            <ul className="list" data-testid="draft-list">
              {drafts.map((d) => (
                <li key={d.id} className="draft-row">
                  <button type="button" className="list-item" onClick={() => onOpen(d.id)} data-testid="draft-item">
                    <span className="li-title">{d.name}</span>
                    <span className="li-meta">
                      <AspectBadge canvas={d.canvas} /> {d.canvas.width} × {d.canvas.height} · {t('start.revision', { n: d.revision })} · {new Date(d.updatedAt).toLocaleString()}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t('start.delete', { name: d.name })}
                    title={t('start.delete', { name: d.name })}
                    onClick={async () => {
                      if (!window.confirm(t('start.confirmDelete', { name: d.name }))) return;
                      await api.deleteDraft(d.id);
                      load();
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <footer className="start-foot">
        <VersionLabel />
      </footer>
    </div>
  );
}
